import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from 'react-query';
import { Outlet } from 'react-router-dom';
import { useAppSelector } from '../../../hook/store';
import { ESliceAuth } from '../../../store/slices/authSlice';
import { APIGetAccountingFranchises } from '../../../service/api/APIAccountingFranchises';

const readSelection = () => {
  try { return localStorage.getItem('currentFranchiseUuid')?.toLowerCase() || ''; }
  catch { return ''; }
};

/** Legacy accounting hooks do not all include actor/franchise in their keys.
 * A fresh cache per scope prevents a previous account's invoices resurfacing. */
function AccountingScope() {
  const parent = useQueryClient();
  const [client] = useState(() => new QueryClient({ defaultOptions: parent.getDefaultOptions() }));
  useEffect(() => () => { client.clear(); }, [client]);
  return <QueryClientProvider client={client}><Outlet /></QueryClientProvider>;
}

function ResolveAccountingFranchise({ actor }: { actor: string }) {
  const [selected, setSelected] = useState(readSelection);
  const [ready, setReady] = useState('');
  const [storageError, setStorageError] = useState(false);
  const available = useQuery(['accounting-available-franchises', actor],
    ({ signal }) => APIGetAccountingFranchises(signal),
    { staleTime: 0, cacheTime: 0, retry: false, refetchOnWindowFocus: true });
  const options = available.data || [];
  const franchise = !available.isError && options.length
    ? options.find(item => item.uuid === selected)?.uuid || (options.length === 1 ? options[0].uuid : '')
    : '';

  useEffect(() => {
    const refresh = () => setSelected(readSelection());
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Mount consumers only after their transport context has been persisted.
  // Children remount with a clean cache whenever the actor or franchise changes.
  useLayoutEffect(() => {
    setReady('');
    if (!franchise) return;
    try {
      localStorage.setItem('currentFranchiseUuid', franchise);
      setSelected(franchise);
      setStorageError(false);
      setReady(franchise);
    } catch { setStorageError(true); }
  }, [franchise, selected]);

  if (available.isLoading) return <Box p={3} role="status"><CircularProgress size={20} /> กำลังตรวจสอบแฟรนไชส์ที่เข้าถึงได้</Box>;
  if (available.isError) return <Box p={3}><Alert severity="error" action={<Button color="inherit" onClick={() => available.refetch()}>ลองใหม่</Button>}>ตรวจสอบแฟรนไชส์ไม่สำเร็จ กรุณาลองใหม่</Alert></Box>;
  if (!options.length) return <Box p={3}><Alert severity="warning">บัญชีนี้ยังไม่มีแฟรนไชส์ที่เข้าถึงได้ กรุณาติดต่อผู้ดูแลระบบ</Alert></Box>;
  if (storageError) return <Box p={3}><Alert severity="error">บันทึกแฟรนไชส์ในเบราว์เซอร์ไม่ได้ กรุณาอนุญาตพื้นที่จัดเก็บแล้วโหลดหน้าใหม่</Alert></Box>;

  return <>
    {options.length > 1 && <Box px={3} pt={2}>
      <TextField select SelectProps={{ native: true }} size="small" label="แฟรนไชส์สำหรับงานบัญชี" value={franchise}
        onChange={event => setSelected(event.target.value)} sx={{ minWidth: 260, maxWidth: '100%' }}>
        <option value="" disabled>เลือกแฟรนไชส์</option>
        {options.map(item => <option key={item.uuid} value={item.uuid}>{item.name}</option>)}
      </TextField>
      {!franchise && <Alert severity="info" sx={{ mt: 2 }}>เลือกแฟรนไชส์เพื่อดูรายการและจัดการเอกสาร</Alert>}
    </Box>}
    {franchise && selected === franchise && ready === franchise && <AccountingScope key={actor + ':' + franchise} />}
  </>;
}

export default function AccountingFranchiseBoundary() {
  const auth = useAppSelector(state => state[ESliceAuth.AuthInfo]);
  const actor = JSON.stringify([auth.username, auth.email]);
  if (!auth.access_token) return null;
  return <ResolveAccountingFranchise key={actor} actor={actor} />;
}
